/**
 * Pure normalization of a output entry's persisted `metadata_snapshot` column.
 *
 * The backend writes that column in TWO different encodings, and both reach the
 * client on the same field:
 *
 * 1. **Ordered pair array** — what an auto-resolve produces
 *    (`DeliveryOutputMetadata.snapshotJson`):
 *    `[{ "customerKey": "operator_full_name", "value": "Jane" }, { "customerKey": "shift", "value": null }]`
 *    An array is used server-side to preserve the binding's declaration order and
 *    mirror the pure `Snapshot` list; `value` is JSON `null` for a key the asset
 *    has not filled.
 * 2. **Plain object map** — what a manual `PATCH`/`PUT .../metadata` writes
 *    (`patchEntrySnapshot` merges into an object): `{ "operator_full_name": "Jane" }`.
 *
 * Everything downstream (the entry row's field count, the metadata form's
 * prefill, the PATCH-merge body) speaks `customerKey → value`, so both forms are
 * normalized onto that record here — insertion order is preserved, keeping the
 * binding order the array encoding carries.
 *
 */
import type {
  MetadataSnapshot,
  MetadataSnapshotResponse,
  OutputEntryMetadataResult,
  OutputEntryMetadataResultResponse,
  OutputEntryPage,
  OutputEntryPageResponse,
  OutputEntryView,
  OutputEntryViewResponse,
} from '@/features/delivery/types/outputContracts';

function normalizeMetadataSnapshot(raw: MetadataSnapshotResponse): MetadataSnapshot {
  return Array.isArray(raw)
    ? Object.fromEntries(raw.map(({ customerKey, value }) => [customerKey, value]))
    : (raw as MetadataSnapshot);
}

function toOutputEntry(response: OutputEntryViewResponse): OutputEntryView {
  const { metadataSnapshot, ...entry } = response;
  return {
    ...entry,
    ...(metadataSnapshot === undefined
      ? {}
      : {
          metadataSnapshot:
            metadataSnapshot === null ? null : normalizeMetadataSnapshot(metadataSnapshot),
        }),
  };
}

export function toOutputEntryPage(response: OutputEntryPageResponse): OutputEntryPage {
  return { ...response, items: response.items.map(toOutputEntry) };
}

export function toOutputEntryMetadataResult(
  response: OutputEntryMetadataResultResponse,
): OutputEntryMetadataResult {
  const { metadataSnapshot, ...result } = response;
  return {
    ...result,
    ...(metadataSnapshot === undefined
      ? {}
      : {
          metadataSnapshot:
            metadataSnapshot === null ? null : normalizeMetadataSnapshot(metadataSnapshot),
        }),
  };
}

/**
 * Whether the entry's DELIVERY binds customer metadata at all.
 *
 * The binding is delivery-wide; the snapshot is baked PER ENTRY (at add time, and
 * again by `POST .../metadata/resolve`). So the two states behind an empty snapshot
 * are different problems, and only this flag separates them:
 *
 * - **no binding** — nothing was ever going to be resolved; an empty snapshot is
 *   the expected, uninteresting outcome.
 * - **binding, empty snapshot** — sibling entries of the same delivery carry fields
 *   and this one does not, because its snapshot was never baked (it was added before
 *   the binding existed, or a resolve has not run). That is actionable: `Resolve
 *   metadata` re-bakes it.
 *
 * `not_evaluated` is the backend's explicit "no active binding" result. Any other
 * status means the backend evaluated the entry against a binding; missing status
 * remains fail-closed for a partially deployed server.
 */
export function entryHasMetadataBinding(entry: { metadataStatus?: string }): boolean {
  return entry.metadataStatus !== 'not_evaluated';
}
